/**
 * Shared diet classifier — the single source of truth for the fish / veg / meat
 * classification used by the weekly planner pills, the week generator, and the
 * server-side recipe enrichment/backfill.
 *
 * The `diets` tags always win. When they are empty or inconclusive (the common
 * case for scraped recipes, which almost never carry diet tags), we fall back to
 * an FR/EN keyword heuristic over the title + ingredients. Anything we cannot
 * classify defaults to `meat`.
 *
 * These keyword helpers were previously private to `weekGeneratorService.ts`;
 * they live here now so both the client and the `api/` routes can share one
 * implementation. `weekGeneratorService.ts` re-exports them for its own callers
 * and tests.
 */

/** The 3-way diet classification used by the generator and the config UI. */
export type DietKind = 'fish' | 'veg' | 'meat';

/**
 * The minimal structural shape the classifier needs. Both `RecipeIndexEntry` and
 * `PersonalRecipeIndexEntry` (which carry `ingredients: string[]`) satisfy it;
 * callers holding a full `Recipe` should map `extendedIngredients` to names.
 */
export interface ClassifiableRecipe {
  title: string;
  ingredients: string[];
  diets?: string[];
}

// Protein categories for variety tracking + content classification.
// Order matters: `detectProtein` returns the first matching category, so more
// specific meats/fish come before the vegetarian catch-alls.
export const PROTEIN_KEYWORDS: Record<string, string[]> = {
  chicken: ['chicken', 'poulet', 'poultry', 'hen'],
  beef: ['beef', 'boeuf', 'bœuf', 'steak', 'ground beef', 'brisket', 'veal', 'veau'],
  pork: ['pork', 'porc', 'bacon', 'ham', 'jambon', 'sausage', 'saucisse', 'chorizo'],
  fish: ['fish', 'poisson', 'salmon', 'saumon', 'tuna', 'thon', 'cod', 'morue', 'tilapia', 'halibut', 'trout', 'truite', 'anchovy', 'anchois', 'sardine'],
  seafood: ['shrimp', 'crevette', 'prawn', 'lobster', 'homard', 'crab', 'crabe', 'scallop', 'pétoncle', 'mussel', 'moule', 'clam', 'oyster', 'huître', 'calamari', 'squid', 'octopus', 'poulpe'],
  lamb: ['lamb', 'agneau', 'mutton'],
  turkey: ['turkey', 'dinde'],
  tofu: ['tofu', 'tempeh', 'seitan'],
  eggs: ['egg', 'oeuf', 'omelette', 'frittata', 'quiche'],
  legumes: ['beans', 'lentils', 'chickpeas', 'haricots', 'lentilles', 'pois chiches', 'lentil', 'lentille', 'chickpea', 'pois chiche', 'edamame'],
  vegetarian: ['vegetarian', 'végétarien', 'veggie'],
};

/**
 * Once per recipe object. The planner scores every recipe of the catalogue on
 * every slot, and each of these re-built strings and re-ran keyword regexes
 * for the same recipe: on 1420 recipes that quadrupled a week's generation.
 * A recipe's title, ingredients and diets do not change under a caller.
 */
function perRecipe<R>(fn: (recipe: ClassifiableRecipe) => R): (recipe: ClassifiableRecipe) => R {
  const cache = new WeakMap<ClassifiableRecipe, R>();
  return (recipe) => {
    if (cache.has(recipe)) return cache.get(recipe) as R;
    const value = fn(recipe);
    cache.set(recipe, value);
    return value;
  };
}

const searchTextOf = perRecipe(function searchTextOf(recipe: ClassifiableRecipe): string {
  return [recipe.title.toLowerCase(), ...recipe.ingredients.map((i) => i.toLowerCase())].join(' ');
});

/**
 * Seafood used as a seasoning, not as the protein on the plate. Fish sauce in
 * a pho, anchovies melted into a puttanesca, oyster sauce in a stir-fry: none
 * of them makes a fish dinner, and counting them as one put a puttanesca on a
 * fish Friday (5 of 79 catalogue "fish" dinners). They still keep a dish from
 * being vegetarian — a declared vegetarian does not eat fish sauce.
 */
const SEAFOOD_SEASONING_RE = /\b(?:fish sauce|sauce (?:de |au |aux )?poissons?|nu[oô]c[- ]?m[aâ]m|nam pla|oyster sauce|sauce (?:aux |à l['’]|d['’])?hu[iî]tres?|shrimp paste|p[aâ]te de crevettes?|fish (?:stock|broth)|fumet de poisson|bouillon de poisson)\b/gi;
/** Anchovies are a seasoning in an ingredient list; a title that names them names the dish. */
const ANCHOVY_SEASONING_RE = /\b(?:anchovy paste|p[aâ]te d['’]anchois|anchov(?:y|ies)|anchois)\b/gi;

/** A title with its seafood seasonings taken out ("Pho, sauce de poisson" → "Pho, "). */
export function withoutSeafoodSeasoning(title: string): string {
  return title.replace(SEAFOOD_SEASONING_RE, ' ');
}

/** The text protein detection reads: the recipe with its seafood seasonings taken out. */
const proteinTextOf = perRecipe(function proteinTextOf(recipe: ClassifiableRecipe): string {
  const title = recipe.title.toLowerCase().replace(SEAFOOD_SEASONING_RE, ' ');
  const lines = recipe.ingredients.map((i) =>
    i.toLowerCase().replace(SEAFOOD_SEASONING_RE, ' ').replace(ANCHOVY_SEASONING_RE, ' '));
  return [title, ...lines].join(' ');
});

/** Does the recipe carry a seafood seasoning (fish sauce, anchovies, oyster sauce…)? */
export function hasSeafoodSeasoning(recipe: ClassifiableRecipe): boolean {
  const text = searchTextOf(recipe);
  SEAFOOD_SEASONING_RE.lastIndex = 0;
  ANCHOVY_SEASONING_RE.lastIndex = 0;
  const found = SEAFOOD_SEASONING_RE.test(text) || ANCHOVY_SEASONING_RE.test(text);
  SEAFOOD_SEASONING_RE.lastIndex = 0;
  ANCHOVY_SEASONING_RE.lastIndex = 0;
  return found;
}

/**
 * Keyword match anchored at a word start.
 *
 * A bare `includes()` matched mid-word and mislabelled 117 of 1372 real
 * recipes: "champignons" contains "ham" (→ pork), "boeuf" contains "oeuf"
 * (→ eggs), "déconstruite" contains "truite" (→ fish), "kitchen" contains
 * "hen" (→ chicken). Only the *start* is anchored, so stems still match their
 * plurals and inflections — "lentil" → "lentils", "poulet" → "poulets".
 */
const keywordRegexes = new Map<string, RegExp>();

function matchesKeyword(searchText: string, keyword: string): boolean {
  // Compiled once per keyword: the planner asks for every recipe on every
  // slot, and building the RegExp each time was a third of its run on the
  // 1420-recipe catalogue.
  let re = keywordRegexes.get(keyword);
  if (!re) {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    re = new RegExp(`\\b${escaped}`, 'i');
    keywordRegexes.set(keyword, re);
  }
  return re.test(searchText);
}

/**
 * Detect protein category from a recipe's title + ingredients.
 * Returns the first matching category name, or null when nothing matches.
 */
export const detectProtein = perRecipe(function detectProtein(recipe: ClassifiableRecipe): string | null {
  const searchText = proteinTextOf(recipe);

  for (const [protein, keywords] of Object.entries(PROTEIN_KEYWORDS)) {
    if (keywords.some((keyword) => matchesKeyword(searchText, keyword))) {
      return protein;
    }
  }

  return null;
});

/**
 * Check if a recipe is vegetarian.
 * Checks the diets array, protein detection, and title/ingredients keywords.
 */
export const isVegetarianRecipe = perRecipe(function isVegetarianRecipe(recipe: ClassifiableRecipe): boolean {
  // Check diets array first
  if (recipe.diets?.some((d) =>
    d.toLowerCase().includes('vegetarian') ||
    d.toLowerCase().includes('vegan')
  )) {
    return true;
  }

  // A seasoning is not the protein, but it is still fish: a tofu pho made
  // with fish sauce is not vegetarian.
  if (hasSeafoodSeasoning(recipe)) return false;

  // Check protein detection
  const protein = detectProtein(recipe);
  if (protein === 'vegetarian' || protein === 'tofu' || protein === 'legumes') {
    return true;
  }

  // Check title/ingredients for vegetarian indicators
  const searchText = searchTextOf(recipe);

  // Explicitly vegetarian keywords
  if (searchText.match(/\b(vegetarian|végétarien|veggie|vegan|végan|plant-based)\b/)) {
    return true;
  }

  // Check if it has NO meat proteins (might be vegetarian)
  const meatProteins = ['chicken', 'beef', 'pork', 'fish', 'seafood', 'lamb', 'turkey'];
  const hasMeat = meatProteins.some((meat) => {
    const keywords = PROTEIN_KEYWORDS[meat] || [];
    return keywords.some((kw) => matchesKeyword(searchText, kw));
  });

  // If no meat and has vegetarian protein sources, it's vegetarian
  if (!hasMeat && (protein === 'tofu' || protein === 'legumes' || protein === 'eggs')) {
    return true;
  }

  return false;
});

/**
 * Check if a recipe is fish/seafood based.
 */
export function isFishRecipe(recipe: ClassifiableRecipe): boolean {
  const protein = detectProtein(recipe);
  return protein === 'fish' || protein === 'seafood';
}

/**
 * Canonical 3-way classification. Tags win; otherwise fall back to the FR/EN
 * heuristic (vegetarian before fish, so a meatless recipe is never mislabelled
 * fish); anything unclassified defaults to meat.
 */
export const classifyDietKind = perRecipe(function classifyDietKind(recipe: ClassifiableRecipe): DietKind {
  const diets = (recipe.diets ?? []).map((d) => d.toLowerCase());
  if (diets.some((d) => d.includes('vegetarian') || d.includes('vegan'))) return 'veg';
  if (diets.some((d) => d.includes('pescatarian') || d.includes('seafood') || d.includes('fish'))) return 'fish';

  // Title-first. The title names the hero protein, so it outranks incidental
  // ingredients. `detectProtein` scans meat categories before fish/seafood, so
  // a shrimp soup whose ingredients list "bouillon de poulet" (chicken broth)
  // would otherwise detect chicken → meat and lose its fish pill (#476 QA).
  const titleOnly: ClassifiableRecipe = { title: recipe.title, ingredients: [], diets: recipe.diets };
  if (isVegetarianRecipe(titleOnly)) return 'veg';
  if (isFishRecipe(titleOnly)) return 'fish';

  // Fall back to the full title+ingredients heuristic when the title is
  // inconclusive (e.g. "Weeknight bowl" whose ingredients name the protein).
  if (isVegetarianRecipe(recipe)) return 'veg';
  if (isFishRecipe(recipe)) return 'fish';
  return 'meat';
});

/** The dietary restrictions the cook can declare in Réglages → Préférences. */
export type DietaryRestriction =
  | 'vegetarian' | 'vegan' | 'gluten-free' | 'dairy-free'
  | 'keto' | 'paleo' | 'low-carb' | 'mediterranean';

/**
 * Does a recipe satisfy EVERY declared restriction? The one implementation —
 * the recipes screen, the planner's « Régénérer » and the dinner generator all
 * call this, so a cook who declared « végétarien » cannot be served meat on one
 * screen and spared it on another.
 *
 * `vegetarian` and `vegan` go through the ingredient classifier rather than the
 * `diets` tags: scraped recipes almost never carry tags, and a tag-only test
 * found 32 vegetarian recipes in a library that actually holds 271. The other
 * restrictions have no classifier, so a missing tag means "not proven" and the
 * recipe is excluded — a declared gluten-free cook gets a smaller week, never a
 * baguette.
 */
export function satisfiesDietaryRestrictions(
  recipe: ClassifiableRecipe,
  restrictions: readonly string[],
): boolean {
  if (restrictions.length === 0) return true;
  const diets = (recipe.diets ?? []).map((d) => d.toLowerCase());
  return restrictions.every((restriction) => {
    switch (restriction) {
      case 'vegetarian': return isVegetarianRecipe(recipe);
      case 'vegan': return diets.some((d) => d.includes('vegan'));
      case 'gluten-free': return diets.some((d) => d.includes('gluten'));
      case 'dairy-free': return diets.some((d) => d.includes('dairy'));
      case 'keto': return diets.some((d) => d.includes('keto'));
      case 'paleo': return diets.some((d) => d.includes('paleo'));
      case 'low-carb': return diets.some((d) => d.includes('low-carb') || d.includes('lowcarb'));
      case 'mediterranean': return diets.some((d) => d.includes('mediterranean'));
      default: return true;
    }
  });
}
